import { useContext } from 'react';

import { CallSupport } from '@eneco/flows/components';
import { useRouter } from '@eneco/next';
import { GrowthBookContext } from '@growthbook/growthbook-react';

import {
  callSupportExperimentFeatureToggleKey,
  callSupportExperimentVariant,
} from '../../growthbook/experiment.helpers';

/**
 * Gates the call-support widget behind a 50/50 GrowthBook experiment.
 *
 * The widget is a global sibling of the flow router rather than a route, action or context change,
 * so it is gated by a direct feature read instead of the `testConfigs` merge in `experiments/index.ts`.
 * Evaluating the feature here — inside the flow provider, mounted for the whole flow — means exactly
 * one exposure is recorded per flow load; GrowthBook dedupes its `trackingCallback` per
 * experiment + result, so navigating between steps does not re-fire it.
 */
export const CallSupportExperiment = () => {
  const { query } = useRouter();
  // Read the GrowthBook context directly instead of useFeatureValue: the latter throws
  // "Missing or invalid GrowthBookProvider" when no provider is present.
  const { growthbook } = useContext(GrowthBookContext);

  // QA overrides are checked before the feature read so forced sessions record no exposure
  // and therefore stay out of the experiment results.
  if (query.forceCallSupport === 'true') return <CallSupport />;
  if (query.disableExperiments === 'true') return null;

  // Defaults to the control: if the experiments API fails or is slow, nobody sees the widget.
  const variant = growthbook?.getFeatureValue(callSupportExperimentFeatureToggleKey, null);

  return variant === callSupportExperimentVariant ? <CallSupport /> : null;
};
